import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import type { Renderable } from '@adornis/chemistry/renderable.js';
import '@chancenlogistik/client/components/logistik-component-base-header';
import '@chancenlogistik/client/components/logistik-component-base-page-layout';
import { LogistikRoutes } from '@chancenlogistik/client/routes';
import { ChancenBrief, EventTicket, Furnishing, WorkMaterial, WorkshopPack } from '@chancenwerk/db/LogistikProduct.js';
import { AdvertisingMaterialAPI } from '@chancenwerk/db/api/AdvertisingMaterialAPI.js';
import { LearningMaterialAPI } from '@chancenwerk/db/api/LearningMaterialAPI.js';
import { LogistikProductSetAPI } from '@chancenwerk/db/api/LogistikProductSetAPI.js';
import { TravelCertificateAPI } from '@chancenwerk/db/api/TravelCertificateAPI.js';
import '@chancenwerk/design/components/cr-button.js';
import '@chancenwerk/design/components/cr-card.js';
import '@chancenwerk/design/components/cr-flex.js';
import '@chancenwerk/design/components/cr-grid.js';
import '@chancenwerk/design/components/cr-icon.js';
import '@chancenwerk/design/components/cr-link.js';
import '@chancenwerk/design/components/cr-sidebar.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

/**
 * @customElement logistik-page-landing
 */
@customElement('logistik-page-landing')
export class LogistikPageLanding extends ChemistryLitElement {
  @state() protected readonly _learningMaterialCount = new RXController(this, LearningMaterialAPI.count()());
  @state() protected readonly _workmaterialCount = new RXController(
    this,
    WorkMaterial.subscribePublishedWorkMaterialsCount()(),
  );
  @state() protected readonly _advertisingMaterialCount = new RXController(this, AdvertisingMaterialAPI.count()());
  @state() protected readonly _ticketsCount = new RXController(
    this,
    EventTicket.subscribePublishedEventTicketsCount()(),
  );
  @state() protected readonly _furnishingCount = new RXController(
    this,
    Furnishing.subscribePublishedFurnishingsCount()(),
  );
  @state() protected readonly _chancenBriefCount = new RXController(
    this,
    ChancenBrief.subscribePublishedChancenBriefeCount()(),
  );
  @state() protected readonly _chancenakademiePaketCount = new RXController(
    this,
    WorkshopPack.subscribePublishedWorkshopPackCount()(),
  );
  @state() protected readonly _logistikProductSetCount = new RXController(this, LogistikProductSetAPI.count()());
  @state() protected readonly travelCertificateCount = new RXController(this, TravelCertificateAPI.count()());

  override render() {
    return html`
      <logistik-component-base-page-layout>
        <cr-flex space="lg" center ${css({ width: '100%' })}>
          <cr-h2> Willkommen in der CHANCENlogistik </cr-h2>
          <cr-grid
            horizontal
            space="md"
            auto-fill-columns="350px"
            ${acss({
              'cr-card': { cursor: 'pointer', background: this.colors.accent, color: this.colors.light, width: '100%' },
              'cr-icon': { fontSize: this.spacing.xl },
            })}
          >
            ${this._learningMaterialCount.value
              ? this._card(LogistikRoutes.learningMaterials, 'library_books', 'CHANCENhefte')
              : nothing}
            ${this._advertisingMaterialCount.value
              ? this._card(LogistikRoutes.advertisingMaterials, 'campaign', 'Werbe- und Akquisematerialien')
              : nothing}
            ${this._workmaterialCount.value
              ? this._card(LogistikRoutes.workMaterials, 'work', 'Büromaterialien')
              : nothing}
            ${this._ticketsCount.value ? this._card(LogistikRoutes.tickets, 'event', 'Eintrittskarten') : nothing}
            ${this._furnishingCount.value
              ? this._card(LogistikRoutes.furnishingMaterials, 'weekend', 'Mobiliar')
              : nothing}
            ${this._chancenBriefCount.value
              ? this._card(LogistikRoutes.chancenBrief, 'history_edu', 'CHANCENbrief')
              : nothing}
            ${this.travelCertificateCount.value
              ? this._card(LogistikRoutes.travelCertificate, 'explore', 'CHANCENreise-Zertifikate')
              : nothing}
            ${this._chancenakademiePaketCount.value
              ? this._card(LogistikRoutes.workshopPacks, 'inventory_2', 'CHANCENakademie Workshop-Pakete')
              : nothing}
            ${this._logistikProductSetCount.value
              ? this._card(LogistikRoutes.logistikProductSets, 'package_2', 'CHANCENsets')
              : nothing}
            ${this._card(LogistikRoutes.individualOrder, 'star', 'Individuelle Bestellung')}
            ${this._card(LogistikRoutes.entireTrip, 'commute', 'Fahrt und Unterkunft')}
            ${this._card(LogistikRoutes.otherOrder, 'blinds', 'Benötigst du handwerkliche Unterstützung?')}
          </cr-grid>
        </cr-flex>
      </logistik-component-base-page-layout>
    `;
  }

  protected _card(link: string, icon: string, text: string): Renderable {
    return html`
      <cr-link href=${link}>
        <cr-card href=${link}>
          <cr-flex space="lg" crossaxis-center align="center">
            <cr-icon> ${icon} </cr-icon>
            <cr-text> ${text} </cr-text>
          </cr-flex>
        </cr-card>
      </cr-link>
    `;
  }
}
